import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bot,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  MessageSquare,
  Pencil,
  Play,
  Send,
  Settings as SettingsIcon,
  Sparkles,
  X,
} from "lucide-react";
import type {
  AiEmailPrompt,
  AutopilotConfig,
  AutopilotRun,
  BulkEditProposal,
  ChatMessage,
  EmailDraftWithContact,
} from "@/lib/types";
import { cn } from "@/lib/utils";

function contactName(d: EmailDraftWithContact): string {
  const c = d.contact;
  if (!c) return "Unknown";
  return (
    [c.first_name, c.last_name].filter(Boolean).join(" ") ||
    c.email ||
    "Unknown"
  );
}

function snippet(text: string, max = 140): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max)}...` : flat;
}

export function AutopilotPage() {
  const [drafts, setDrafts] = useState<EmailDraftWithContact[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [runs, setRuns] = useState<AutopilotRun[]>([]);
  const [config, setConfig] = useState<AutopilotConfig | null>(null);
  const [prompts, setPrompts] = useState<AiEmailPrompt[]>([]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Record<string, { subject: string; body_text: string }>>({});

  const [running, setRunning] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<string | null>(null);

  const loadDrafts = useCallback(async () => {
    setDraftsLoading(true);
    try {
      const res = await fetch("/api/autopilot/drafts?status=pending", {
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to load drafts");
      setDrafts(data.drafts ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load drafts");
    } finally {
      setDraftsLoading(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const res = await fetch("/api/autopilot/runs", { credentials: "include" });
      const data = await res.json();
      if (res.ok) setRuns(data.runs ?? []);
    } catch {
      /* non-critical */
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const [configRes, promptsRes] = await Promise.all([
        fetch("/api/autopilot/config", { credentials: "include" }),
        fetch("/api/ai-prompts", { credentials: "include" }),
      ]);
      const configData = await configRes.json();
      const promptsData = await promptsRes.json();
      if (configRes.ok) setConfig(configData.config);
      if (promptsRes.ok) setPrompts(promptsData.prompts ?? []);
    } catch {
      /* non-critical */
    }
  }, []);

  useEffect(() => {
    loadDrafts();
    loadRuns();
    loadConfig();
  }, [loadDrafts, loadRuns, loadConfig]);

  const allSelected = drafts.length > 0 && drafts.every((d) => selected.has(d.id));
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(drafts.map((d) => d.id)));
  }
  function toggleOne(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleExpand(id: string) {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function startEdit(d: EmailDraftWithContact) {
    setEditing((e) => ({
      ...e,
      [d.id]: { subject: d.subject, body_text: d.body_text },
    }));
  }
  function cancelEdit(id: string) {
    setEditing((e) => {
      const next = { ...e };
      delete next[id];
      return next;
    });
  }
  async function saveEdit(id: string) {
    const draft = editing[id];
    if (!draft) return;
    try {
      const res = await fetch(`/api/autopilot/drafts/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Save failed");
      setDrafts((ds) =>
        ds.map((d) =>
          d.id === id
            ? { ...d, subject: data.draft.subject, body_text: data.draft.body_text }
            : d
        )
      );
      cancelEdit(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  async function runNow() {
    setRunning(true);
    setError(null);
    setActionResult(null);
    try {
      const res = await fetch("/api/autopilot/run", {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Run failed");
      setActionResult(
        `Run complete: ${data.drafts_created} draft(s) created from ${data.prospects_found} prospect(s) in ${data.city}.`
      );
      await Promise.all([loadDrafts(), loadRuns()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Run failed");
    } finally {
      setRunning(false);
    }
  }

  async function approveSelected() {
    if (selected.size === 0) return;
    setApproving(true);
    setError(null);
    setActionResult(null);
    try {
      const res = await fetch("/api/autopilot/drafts/approve", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ draft_ids: Array.from(selected) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Approve failed");
      setActionResult(
        `Sent ${data.sent.length} email(s)${
          data.failed.length ? `, ${data.failed.length} failed` : ""
        }.`
      );
      setSelected(new Set());
      await loadDrafts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approve failed");
    } finally {
      setApproving(false);
    }
  }

  async function rejectSelected() {
    if (selected.size === 0) return;
    setRejecting(true);
    setError(null);
    setActionResult(null);
    try {
      const res = await fetch("/api/autopilot/drafts/reject", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ draft_ids: Array.from(selected) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Reject failed");
      setActionResult(`Rejected ${data.count} draft(s).`);
      setSelected(new Set());
      await loadDrafts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reject failed");
    } finally {
      setRejecting(false);
    }
  }

  const lastRun = runs[0] ?? null;

  return (
    <div className="mx-auto max-w-6xl p-8 pb-32">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Bot className="h-6 w-6 text-primary" />
            Autopilot
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Discovers prospects each night, drafts emails for your review, and
            sends on approval.
          </p>
          {lastRun && (
            <p className="mt-1 text-xs text-muted-foreground">
              Last run:{" "}
              {new Date(lastRun.started_at).toLocaleString()} -{" "}
              {lastRun.error ? (
                <span className="text-destructive">error: {lastRun.error}</span>
              ) : (
                <>
                  {lastRun.drafts_created} drafts from {lastRun.prospects_found}{" "}
                  prospects {lastRun.city ? `in ${lastRun.city}` : ""}
                </>
              )}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            className="flex items-center gap-1.5 rounded-md border bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50"
          >
            <SettingsIcon className="h-4 w-4" />
            Configure
          </button>
          <button
            type="button"
            onClick={runNow}
            disabled={running}
            className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {running ? "Running..." : "Run Now"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {actionResult && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
          {actionResult}
        </div>
      )}

      <SummaryCard draftCount={drafts.length} draftsKey={drafts.map((d) => d.id).join(",")} />

      <ChatPanel
        draftCount={drafts.length}
        selectedDraftIds={selected.size > 0 ? Array.from(selected) : undefined}
        onDraftsChanged={loadDrafts}
      />

      <div className="mt-6 rounded-xl border bg-card">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold">
              Pending drafts ({drafts.length})
            </h2>
            {drafts.length > 0 && (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="h-3.5 w-3.5 rounded border-gray-300 accent-primary"
                />
                Select all
              </label>
            )}
          </div>
        </div>

        {draftsLoading ? (
          <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading drafts...
          </div>
        ) : drafts.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            No drafts pending review. Click <strong>Run Now</strong> to discover
            prospects and generate drafts.
          </div>
        ) : (
          <ul className="divide-y">
            {drafts.map((d) => {
              const isSelected = selected.has(d.id);
              const isExpanded = expanded.has(d.id);
              const editingState = editing[d.id];
              return (
                <li
                  key={d.id}
                  className={cn(
                    "transition-colors",
                    isSelected ? "bg-primary/5" : "hover:bg-slate-50/60"
                  )}
                >
                  <div className="flex items-start gap-3 px-6 py-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOne(d.id)}
                      className="mt-1 h-3.5 w-3.5 rounded border-gray-300 accent-primary"
                    />
                    <button
                      type="button"
                      onClick={() => toggleExpand(d.id)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">
                            {contactName(d)}
                            {d.contact?.company ? (
                              <span className="ml-2 text-xs text-muted-foreground">
                                · {d.contact.company}
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-0.5 truncate text-sm text-foreground">
                            {d.subject}
                          </div>
                          {!isExpanded && (
                            <div className="mt-1 truncate text-xs text-muted-foreground">
                              {snippet(d.body_text)}
                            </div>
                          )}
                        </div>
                        <div className="shrink-0 text-xs text-muted-foreground">
                          {d.contact?.email}
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleExpand(d.id)}
                      className="rounded p-1 text-muted-foreground hover:bg-slate-100"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="border-t bg-slate-50/40 px-6 py-4">
                      {editingState ? (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={editingState.subject}
                            onChange={(e) =>
                              setEditing((s) => ({
                                ...s,
                                [d.id]: {
                                  ...editingState,
                                  subject: e.target.value,
                                },
                              }))
                            }
                            className="w-full rounded-md border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                          />
                          <textarea
                            rows={10}
                            value={editingState.body_text}
                            onChange={(e) =>
                              setEditing((s) => ({
                                ...s,
                                [d.id]: {
                                  ...editingState,
                                  body_text: e.target.value,
                                },
                              }))
                            }
                            className="w-full rounded-md border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => saveEdit(d.id)}
                              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => cancelEdit(d.id)}
                              className="rounded-md border bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="mb-2 flex items-center justify-between">
                            <div className="text-xs text-muted-foreground">
                              {d.contact?.title}
                              {d.contact?.industry
                                ? ` · ${d.contact.industry}`
                                : ""}
                              {d.contact?.location
                                ? ` · ${d.contact.location}`
                                : ""}
                            </div>
                            <button
                              type="button"
                              onClick={() => startEdit(d)}
                              className="flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs font-medium hover:bg-slate-50"
                            >
                              <Pencil className="h-3 w-3" />
                              Edit
                            </button>
                          </div>
                          <pre className="whitespace-pre-wrap font-sans text-sm text-foreground">
                            {d.body_text}
                          </pre>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selected.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-8 py-4">
            <div className="text-sm font-medium">
              {selected.size} draft{selected.size === 1 ? "" : "s"} selected
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="rounded-md border bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={rejectSelected}
                disabled={rejecting || approving}
                className="flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                {rejecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                Reject {selected.size}
              </button>
              <button
                type="button"
                onClick={approveSelected}
                disabled={approving || rejecting}
                className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {approving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Approve & Send {selected.size}
              </button>
            </div>
          </div>
        </div>
      )}

      {settingsOpen && config && (
        <SettingsModal
          config={config}
          prompts={prompts}
          onClose={() => setSettingsOpen(false)}
          onSaved={(c) => {
            setConfig(c);
            setSettingsOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Summary card
// ---------------------------------------------------------------------------

function SummaryCard({
  draftCount,
  draftsKey,
}: {
  draftCount: number;
  draftsKey: string;
}) {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-fetch summary whenever the set of pending drafts changes.
  useEffect(() => {
    if (draftCount === 0) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch("/api/autopilot/drafts/summary", {
          method: "POST",
          credentials: "include",
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(data.error ?? "Summary failed");
        setSummary(data.summary ?? "");
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Summary failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draftCount, draftsKey]);

  if (draftCount === 0) return null;

  return (
    <div className="mb-4 rounded-xl border border-violet-200 bg-violet-50/40">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-5 py-3 text-sm font-medium text-violet-800"
      >
        <span className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          AI Summary
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>
      {open && (
        <div className="border-t border-violet-200 px-5 py-3 text-sm">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Summarizing...
            </div>
          ) : error ? (
            <div className="text-destructive">{error}</div>
          ) : summary ? (
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
              {summary}
            </pre>
          ) : (
            <div className="text-muted-foreground">No summary yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat panel
// ---------------------------------------------------------------------------

function ChatPanel({
  draftCount,
  selectedDraftIds,
  onDraftsChanged,
}: {
  draftCount: number;
  selectedDraftIds?: string[];
  onDraftsChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"ask" | "edit">("ask");
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<BulkEditProposal | null>(null);
  const [applying, setApplying] = useState(false);
  const [expandedEdits, setExpandedEdits] = useState<Set<number>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [history.length, sending, proposal]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const message = input.trim();
    if (!message || sending) return;

    if (mode === "edit") {
      await sendEdit(message);
      return;
    }

    setSending(true);
    setError(null);
    const nextHistory: ChatMessage[] = [
      ...history,
      { role: "user", content: message },
    ];
    setHistory(nextHistory);
    setInput("");
    try {
      const res = await fetch("/api/autopilot/drafts/chat", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, history }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Chat failed");
      setHistory([
        ...nextHistory,
        { role: "assistant", content: data.reply ?? "" },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chat failed");
    } finally {
      setSending(false);
    }
  }

  async function sendEdit(instruction: string) {
    setSending(true);
    setError(null);
    setProposal(null);
    setExpandedEdits(new Set());
    setHistory((h) => [
      ...h,
      { role: "user", content: `[Edit] ${instruction}` },
    ]);
    setInput("");
    try {
      const res = await fetch("/api/autopilot/drafts/bulk-edit", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instruction,
          draft_ids: selectedDraftIds,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Bulk edit failed");
      const changedEdits = (data.edits as BulkEditProposal["edits"]).filter(
        (e) =>
          e.new_subject !== e.original_subject ||
          e.new_body !== e.original_body
      );
      const prop: BulkEditProposal = {
        summary: data.summary,
        edits: changedEdits,
      };
      setProposal(prop);
      setHistory((h) => [
        ...h,
        {
          role: "assistant",
          content: `${data.summary}\n\n${changedEdits.length} draft(s) would be modified. Review the changes below.`,
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bulk edit failed");
    } finally {
      setSending(false);
    }
  }

  async function applyEdits() {
    if (!proposal) return;
    setApplying(true);
    setError(null);
    try {
      const res = await fetch("/api/autopilot/drafts/bulk-edit/apply", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          edits: proposal.edits.map((e) => ({
            id: e.draft_id,
            subject: e.new_subject,
            body_text: e.new_body,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Apply failed");
      setHistory((h) => [
        ...h,
        {
          role: "assistant",
          content: `Applied edits to ${data.updated} draft(s).`,
        },
      ]);
      setProposal(null);
      await onDraftsChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Apply failed");
    } finally {
      setApplying(false);
    }
  }

  function discardProposal() {
    setProposal(null);
    setHistory((h) => [
      ...h,
      { role: "assistant", content: "Edits discarded." },
    ]);
  }

  function toggleEditExpand(i: number) {
    setExpandedEdits((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  const scope = selectedDraftIds
    ? `${selectedDraftIds.length} selected`
    : "all pending";

  return (
    <div className="rounded-xl border bg-card">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-5 py-3 text-sm font-medium"
      >
        <span className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          AI Chat & Bulk Edit
          {draftCount === 0 && (
            <span className="text-xs text-muted-foreground">
              (no pending drafts)
            </span>
          )}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>
      {open && (
        <div className="border-t">
          {/* Mode tabs */}
          <div className="flex border-b px-5">
            <button
              type="button"
              onClick={() => setMode("ask")}
              className={cn(
                "border-b-2 px-3 py-2 text-xs font-medium transition-colors",
                mode === "ask"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <span className="flex items-center gap-1.5">
                <MessageSquare className="h-3 w-3" />
                Ask
              </span>
            </button>
            <button
              type="button"
              onClick={() => setMode("edit")}
              className={cn(
                "border-b-2 px-3 py-2 text-xs font-medium transition-colors",
                mode === "edit"
                  ? "border-violet-600 text-violet-700"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <span className="flex items-center gap-1.5">
                <Pencil className="h-3 w-3" />
                Bulk Edit
                {selectedDraftIds && (
                  <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                    {selectedDraftIds.length}
                  </span>
                )}
              </span>
            </button>
          </div>

          {/* Chat history */}
          <div
            ref={scrollRef}
            className="max-h-96 space-y-3 overflow-y-auto px-5 py-4"
          >
            {history.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {mode === "ask" ? (
                  <>
                    Try: <em>"Which drafts mention pricing?"</em> or{" "}
                    <em>"Are any of these too aggressive?"</em>
                  </>
                ) : (
                  <>
                    Describe how to edit {scope} drafts, e.g.:{" "}
                    <em>"Make the subject lines shorter and punchier"</em> or{" "}
                    <em>"Add a mention of our free trial"</em>
                  </>
                )}
              </div>
            ) : (
              history.map((m, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex",
                    m.role === "user" ? "justify-end" : "justify-start"
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm",
                      m.role === "user"
                        ? m.content.startsWith("[Edit]")
                          ? "bg-violet-600 text-white"
                          : "bg-primary text-primary-foreground"
                        : "bg-slate-100 text-foreground"
                    )}
                  >
                    {m.content}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {mode === "edit"
                    ? "Generating edits..."
                    : "Thinking..."}
                </div>
              </div>
            )}
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          {/* Proposal preview */}
          {proposal && proposal.edits.length > 0 && (
            <div className="border-t border-violet-200 bg-violet-50/30">
              <div className="px-5 py-3">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-violet-700">
                    Proposed changes ({proposal.edits.length})
                  </h4>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={discardProposal}
                      disabled={applying}
                      className="rounded-md border bg-white px-2.5 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
                    >
                      Discard
                    </button>
                    <button
                      type="button"
                      onClick={applyEdits}
                      disabled={applying}
                      className="flex items-center gap-1.5 rounded-md bg-violet-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                    >
                      {applying ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Check className="h-3 w-3" />
                      )}
                      Apply All
                    </button>
                  </div>
                </div>
                <div className="max-h-64 space-y-2 overflow-y-auto">
                  {proposal.edits.map((edit, i) => {
                    const isOpen = expandedEdits.has(i);
                    const subjectChanged =
                      edit.new_subject !== edit.original_subject;
                    const bodyChanged =
                      edit.new_body !== edit.original_body;
                    return (
                      <div
                        key={edit.draft_id}
                        className="rounded-lg border bg-white"
                      >
                        <button
                          type="button"
                          onClick={() => toggleEditExpand(i)}
                          className="flex w-full items-center justify-between px-3 py-2 text-left text-xs"
                        >
                          <span className="font-medium">
                            {edit.contact_name}
                            {subjectChanged && (
                              <span className="ml-2 text-amber-600">
                                subject changed
                              </span>
                            )}
                            {bodyChanged && (
                              <span className="ml-2 text-amber-600">
                                body changed
                              </span>
                            )}
                          </span>
                          {isOpen ? (
                            <ChevronUp className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="space-y-2 border-t px-3 py-2">
                            {subjectChanged && (
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                  Subject
                                </div>
                                <div className="mt-0.5 rounded bg-red-50 px-2 py-1 text-xs text-red-800 line-through">
                                  {edit.original_subject}
                                </div>
                                <div className="mt-0.5 rounded bg-emerald-50 px-2 py-1 text-xs text-emerald-800">
                                  {edit.new_subject}
                                </div>
                              </div>
                            )}
                            {bodyChanged && (
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                  Body
                                </div>
                                <div className="mt-0.5 max-h-24 overflow-y-auto rounded bg-red-50 px-2 py-1 text-xs leading-relaxed text-red-800 line-through">
                                  {edit.original_body}
                                </div>
                                <div className="mt-0.5 max-h-24 overflow-y-auto rounded bg-emerald-50 px-2 py-1 text-xs leading-relaxed text-emerald-800">
                                  {edit.new_body}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={send}
            className="flex items-center gap-2 border-t px-5 py-3"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                mode === "edit"
                  ? `Describe edits for ${scope} drafts...`
                  : "Ask about the drafts..."
              }
              className={cn(
                "flex-1 rounded-md border px-3 py-2 text-sm outline-none focus:ring-2",
                mode === "edit"
                  ? "focus:ring-violet-300"
                  : "focus:ring-ring"
              )}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50",
                mode === "edit"
                  ? "bg-violet-600 text-white hover:bg-violet-700"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              )}
            >
              {mode === "edit" ? (
                <Sparkles className="h-3.5 w-3.5" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              {mode === "edit" ? "Edit" : "Send"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings modal
// ---------------------------------------------------------------------------

function SettingsModal({
  config,
  prompts,
  onClose,
  onSaved,
}: {
  config: AutopilotConfig;
  prompts: AiEmailPrompt[];
  onClose: () => void;
  onSaved: (c: AutopilotConfig) => void;
}) {
  const [template, setTemplate] = useState(config.query_template);
  const [citiesText, setCitiesText] = useState(config.cities.join(", "));
  const [promptId, setPromptId] = useState(config.ai_prompt_id ?? "");
  const [enabled, setEnabled] = useState(config.enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cities = useMemo(
    () =>
      citiesText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [citiesText]
  );

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/autopilot/config", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query_template: template,
          cities,
          ai_prompt_id: promptId || null,
          enabled,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Save failed");
      onSaved(data.config);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-xl border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-base font-semibold">Autopilot Settings</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-6">
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Query template (use <code>{"{city_macro}"}</code> for the city)
            </label>
            <textarea
              rows={3}
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Cities (comma-separated, cycled one per night)
            </label>
            <textarea
              rows={2}
              value={citiesText}
              onChange={(e) => setCitiesText(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {cities.length} cit{cities.length === 1 ? "y" : "ies"} configured.
              Next up:{" "}
              {cities[config.current_city_index % Math.max(cities.length, 1)] ??
                "(none)"}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              AI prompt for drafting emails
            </label>
            <select
              value={promptId}
              onChange={(e) => setPromptId(e.target.value)}
              className="mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select a prompt...</option>
              {prompts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {prompts.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                No prompts saved yet. Create one from the GTM Engine email
                composer.
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 accent-primary"
            />
            Enable nightly schedule
          </label>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
